import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { formatCurrency } from '@/libs/antd/formater';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	PrintDisbursementSearchPOOrderParam,
	useDownloadPODocument,
	usePrintDisbursementSearchPOOptionQuery,
	usePrintDisbursementSearchPOOrderQuery,
} from '@/libs/fleet-api/services/print-disbursement';
import { usePrintPurchaseOrder } from '@/libs/fleet-api/hooks/purchase-order';
import {
	ArrowRightOutlined,
	FileSearchOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
} from 'antd';
import Checkbox from 'antd/es/checkbox/Checkbox';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';

const DisbursementPOListPage: FC<{
	mppId: number;
}> = ({ mppId }) => {
	const { navigate } = useApp();
	const {
		modal,
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
	} = useModals();
	const userData = useUserData();
	const { groupName } = userData ?? {};

	const { form, fieldName, value } = useForm<{
		searchType: string;
		searchParam: PrintDisbursementSearchPOOrderParam;
	}>();

	const searchOptionQuery = usePrintDisbursementSearchPOOptionQuery();
	const {
		fcohStatusOptions = [],
		purchaseOrderStatusOptions = [],
		searchByOptions = [],
	} = searchOptionQuery.data?.data ?? {};

	const defaultSeachParams: PrintDisbursementSearchPOOrderParam = {
		mppId,
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	};

	const [searchParam, setSearchParam] =
		useState<PrintDisbursementSearchPOOrderParam>(defaultSeachParams);

	const { data, isFetching } =
		usePrintDisbursementSearchPOOrderQuery(searchParam);

	const { items = [], mainInformation } = data?.data ?? {};
	const { mppNo, customerId, customerName, bmppGroupId, bmppGroupName } =
		mainInformation ?? {};

	const dataSource = items.map((item, key) => ({ ...item, key }));

	const downloadPODocument = useDownloadPODocument();
	const printPurchaseOrderMutation = usePrintPurchaseOrder();

	const [selectedPurchaseOrderIds, setSelectedPurchaseOrderIds] = useState<
		number[]
	>([]);
	const purchaseOrderIds = selectedPurchaseOrderIds;

	const selectedItems = dataSource?.filter((d) =>
		selectedPurchaseOrderIds.includes(d.purchaseOrderId),
	);

	const financingProductId = selectedItems?.[0]?.financingProduct?.id;

	const renderSearchType = () => {
		switch (value?.searchType) {
			case 'simulation-no':
			case 'po-no':
			case 'application-id':
			case 'agreement-no':
			case 'plakat-no':
			case 'supplier-name':
			case 'application-status':
				return (
					<Form.Item name={fieldName(['searchParam', 'search', 'value'])}>
						<Input />
					</Form.Item>
				);
			case 'po-date':
				return (
					<Form.Item
						name={fieldName(['searchParam', 'purchaseOrderDate'])}
						getValueProps={(value) => ({
							value: value && dayjs(value),
						})}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
					>
						<DatePicker format={'DD/MM/YYYY'} />
					</Form.Item>
				);
			case 'fcoh-verification-status':
				return (
					<Form.Item
						name={fieldName(['searchParam', 'fcohVerificationStatus'])}
					>
						<Select
							options={fcohStatusOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			case 'po-status':
				return (
					<Form.Item name={fieldName(['searchParam', 'purchaseOrderStatus'])}>
						<Select
							options={purchaseOrderStatusOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			default:
				return (
					<Form.Item>
						<Input disabled />
					</Form.Item>
				);
		}
	};

	return (
		<div>
			<FleetTitle style={{ marginBottom: 20 }}>
				{groupName === 'FCDVS'
					? 'Dokumen Kesepakatan Realisasi Pencairan Pembiayaan'
					: 'Pencetakan Dokumen Pencairan'}
			</FleetTitle>

			<DataRow
				loading={isFetching}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!customerId) return;
									openCustomerDetail(String(customerId));
								}}
							>
								{customerName}
							</a>,
						],
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
					],
					[
						[
							'No MPP',
							mppNo && mppId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('MPPPage', {
											mppId,
											viewOnly: true,
											onBack: () => {
												navigate('DisbursementPOListPage', {
													mppId,
												});
											},
										});
									}}
								>
									{mppNo}
								</a>
							) : (
								'-'
							),
						],
						[
							'View Historical PO',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									navigate('DisbursementPOHistory', {
										id: mppId,
										onBack: () => {
											navigate('DisbursementPOListPage', {
												mppId,
											});
										},
									});
								}}
							>
								View
							</a>,
						],
					],
				]}
			/>

			<Divider />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(values) => {
					if (values.searchType) {
						if (
							[
								'simulation-no',
								'po-no',
								'application-id',
								'agreement-no',
								'plakat-no',
								'supplier-name',
								'application-status',
							].includes(values.searchType)
						) {
							values.searchParam.search!.by = values.searchType;
						}

						setSearchParam({
							...values.searchParam,
							mppId,
							pagination: {
								pageNumber: 1,
								pageSize: 10,
							},
						});
					} else {
						setSearchParam(defaultSeachParams);
					}
				}}
			>
				<Row gutter={[20, 20]}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchType')}
							label={<span>Kata Kunci</span>}
							labelCol={{ flex: 'auto', style: { width: 150 } }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								loading={searchOptionQuery.isLoading}
								disabled={isFetching}
								placeholder="Search By"
								options={searchByOptions.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
								allowClear
								style={{
									display: 'block',
									width: '100%',
								}}
								onSelect={() => {
									form.resetFields([fieldName('searchParam')]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">{renderSearchType()}</Col>
					<Col>
						<Form.Item>
							<Button
								block
								loading={isFetching}
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Divider />
			<Table
				bordered
				scroll={{ x: 'max-content' }}
				loading={isFetching}
				dataSource={dataSource}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No. Simulasi',
						render: (_, record) =>
							record.simulationNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('SimulationPage', {
											simulationId: record.simulationId,
											showResult: true,
											viewOnly: true,
											onBack: () => {
												navigate('DisbursementPOListPage', {
													mppId,
												});
											},
										});
									}}
									style={{ cursor: 'pointer' }}
								>
									{record.simulationNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'No. PO',
						render: (_, record) =>
							record.purchaseOrderNo ? (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										const documents =
											await printPurchaseOrderMutation.mutateAsync({
												purchaseOrderIds: [record.purchaseOrderId],
											});

										openDocumentModal(documents);
									}}
								>
									{record.purchaseOrderNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Skema Pembiayaan',
						render: (_, record) => record.financingProduct?.value ?? '-',
					},
					{
						title: 'Application ID',
						render: (_, record) => record.applicationId ?? '-',
					},
					{
						title: 'Agreement No',
						render: (_, record) => record.agreementNo ?? '-',
					},
					{
						title: 'No Plakat',
						render: (_, record) => record.plakatNo ?? '-',
					},
					{
						title: 'Supplier Name',
						render: (_, record) => record.supplierName ?? '-',
					},
					{
						title: 'Contact Person',
						render: (_, record) => record.picName ?? '-',
					},
					{
						title: 'Supplier Phone',
						render: (_, record) => record.supplierPhone ?? '-',
					},
					{
						title: 'Asset',
						render: (_, record) => record.asset ?? '-',
					},
					{
						title: 'Jumlah Unit ',
						align: 'center',
						render: (_, record) => record.unit ?? '-',
					},
					{
						title: 'NTF ',
						hidden: groupName !== 'FCOH',
						render: (_, record) =>
							record.ntf ? formatCurrency(record.ntf) : '-',
					},
					{
						title: 'PO Date',
						render: (_, record) =>
							record.purchaseOrderDate
								? dayjs(record.purchaseOrderDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'View PO',
						align: 'center',
						fixed: 'right',
						render: (_, record) => {
							if (!record.purchaseOrderId) {
								return <FileSearchOutlined style={{ color: '#bababa' }} />;
							}

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (downloadPODocument.isPending) return;

										const res = await downloadPODocument.mutateAsync([
											record.purchaseOrderId,
										]);

										const [data] = res.data ?? [];
										if (!data) return;

										const link = document.createElement('a');

										link.href = data.url;
										link.click();
										document.body.removeChild(link);
									}}
								>
									{downloadPODocument.isPending &&
									downloadPODocument.variables.includes(
										record.purchaseOrderId,
									) ? (
										<LoadingOutlined />
									) : (
										<FileSearchOutlined />
									)}
								</a>
							);
						},
					},
					{
						title: 'Status PO',
						hidden: groupName !== 'FCOH',
						align: 'center',
						render: (_, record) => record.purchaseOrderStatus ?? '-',
					},
					{
						title: 'Status Verifikasi FCOH',
						align: 'center',
						render: (_, record) => record.verificationStatus ?? '-',
					},
					{
						title: 'Status Aplikasi',
						align: 'center',
						hidden: groupName !== 'FCOH',
						render: (_, record) => record.applicationStatus ?? '-',
					},
					{
						title: 'Cetak Dokumen Pencairan',
						hidden: groupName !== 'FCDVS',
						fixed: 'right',
						align: 'center',
						render: (_, record) => {
							return (
								<Checkbox
									disabled={
										!record.allowCheck ||
										Boolean(
											financingProductId &&
												financingProductId !== record.financingProduct.id,
										)
									}
									checked={selectedPurchaseOrderIds.includes(
										record.purchaseOrderId,
									)}
									onClick={() => {
										setSelectedPurchaseOrderIds((ids) => {
											if (ids.includes(record.purchaseOrderId)) {
												return ids.filter(
													(id) => id !== record.purchaseOrderId,
												);
											}
											return [...ids, record.purchaseOrderId];
										});
									}}
								/>
							);
						},
					},
					{
						hidden: groupName !== 'FCAS',
						title: 'View Dokumen',
						fixed: 'right',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('DisbursementPage', {
											mppId,
											financingProductId: record.financingProduct?.id,
											purchaseOrderIds: [record.purchaseOrderId],
											onBack: () => {
												navigate('DisbursementPOListPage', { mppId });
											},
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
				pagination={false}
			/>
			{(groupName === 'FCOH' || groupName === 'FCDVS') && (
				<Button
					type="primary"
					htmlType="submit"
					style={{
						display: 'block',
						width: 200,
						marginTop: 40,
						marginLeft: 'auto',
					}}
					disabled={
						(groupName === 'FCDVS' && !purchaseOrderIds.length) ||
						!dataSource.length
					}
					onClick={async () => {
						if (groupName === 'FCDVS') {
							const items = dataSource.filter((data) =>
								purchaseOrderIds.includes(data.purchaseOrderId),
							);

							const schemas = [
								...new Set(items.map((item) => item.financingProduct.value)),
							].join(', ');

							const totalUnit = items.reduce(
								(total, item) => total + item.unit,
								0,
							);

							const isConfirmed = await modal.confirm({
								title: 'Apakah Anda ingin melakukan cetak Dokumen Pencairan?',
								cancelText: 'TIDAK',
								okText: 'YA',
								subTitle: (
									<Fragment>
										<div
											style={{
												paddingBottom: 15,
												marginBottom: 20,
												borderBottom: '1px solid #e5e5e5',
											}}
										>
											Berikut adalah detailnya:
										</div>
										<div style={{ textAlign: 'left', color: '#000' }}>
											<Row
												gutter={4}
												wrap={false}
											>
												<Col
													flex="none"
													style={{ width: 150 }}
												>
													Skema Pembiayaan
												</Col>
												<Col flex="none">:</Col>
												<Col flex="auto">
													<b>{schemas}</b>
												</Col>
											</Row>
											<Row
												gutter={4}
												wrap={false}
											>
												<Col
													flex="none"
													style={{ width: 150 }}
												>
													Jumlah Unit
												</Col>
												<Col flex="none">:</Col>
												<Col flex="auto">
													<b>{totalUnit}</b>
												</Col>
											</Row>
										</div>
									</Fragment>
								),
							});

							if (!isConfirmed) return;
						}

						navigate('DisbursementPage', {
							mppId,
							financingProductId,
							purchaseOrderIds,
						});
					}}
				>
					{groupName === 'FCDVS' ? 'CETAK DOKUMEN' : 'NEXT'}
				</Button>
			)}
		</div>
	);
};

export default DisbursementPOListPage;
